'use client';

import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { fetchAPI } from '@/core/api/client';
import { getAbsoluteMediaUrl } from '@/core/utils/media';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useRouter } from '@/core/i18n/navigation';
import { formatPrice } from '@/core/utils/currency';

export interface RecommendedProductImage {
    path: string;
    thumbnail_path?: string | null;
    is_main?: boolean;
}

export interface RecommendedProduct {
    id: number;
    name: string;
    price: number;
    sales_price?: number | null;
    images?: RecommendedProductImage[];
}

function getRecommendedProductImageUrl(prod: RecommendedProduct): string {
    const images = prod.images || [];
    const main = images.find(img => img.is_main) || images[0];
    if (!main) return '';
    return getAbsoluteMediaUrl(main.thumbnail_path || main.path);
}

export interface RecommendationsQuery {
    source?: 'auto' | 'manual' | 'category';
    manualProductIds?: number[];
    categoryId?: string;
    limit?: number;
    excludeProductId?: number;
}

export interface AddToCartModalStyle {
    accentColor?: string;
    primaryBg?: string;
    primaryText?: string;
    secondaryBorderColor?: string;
    secondaryTextColor?: string;
    borderRadius?: number;
}

export interface AddToCartModalProps {
    isOpen: boolean;
    onClose: () => void;
    addedProduct?: {
        name: string;
        brand?: string;
        price?: number;
        image_url?: string | null;
    } | null;
    modalTitle?: string;
    modalBrand?: string;
    recommendationsHeader?: string;
    recommendedProducts?: RecommendedProduct[];
    recommendationsQuery?: RecommendationsQuery;
    continueShoppingText?: string;
    checkoutText?: string;
    style?: AddToCartModalStyle;
}

function buildRecommendationsUrl(query: RecommendationsQuery): string | null {
    const limit = query.limit && query.limit > 0 ? query.limit : 4;
    const params = new URLSearchParams();
    params.set('limit', String(query.excludeProductId ? limit + 1 : limit));

    if (query.source === 'manual') {
        if (!query.manualProductIds || query.manualProductIds.length === 0) return null;
        params.set('ids', query.manualProductIds.join(','));
    } else if (query.source === 'category') {
        if (!query.categoryId) return null;
        params.set('category_id', query.categoryId);
    }

    return `/products?${params.toString()}`;
}

export function AddToCartModal({
    isOpen,
    onClose,
    addedProduct,
    modalTitle,
    modalBrand,
    recommendationsHeader,
    recommendedProducts,
    recommendationsQuery,
    continueShoppingText,
    checkoutText,
    style,
}: AddToCartModalProps) {
    const router = useRouter();
    const { t, currency } = useTranslation();
    const [fetchedItems, setFetchedItems] = useState<RecommendedProduct[]>([]);
    const resolvedModalTitle = modalTitle || t('product.atc_modal.title');
    const resolvedRecommendationsHeader = recommendationsHeader || t('product.atc_modal.header');
    const resolvedContinueShoppingText = continueShoppingText || t('product.atc_modal.continue');
    const resolvedCheckoutText = checkoutText || t('product.atc_modal.checkout');
    const brand = addedProduct?.brand || modalBrand;
    const accentColor = style?.accentColor || '#00c49a';
    const primaryBg = style?.primaryBg || '#0f172a';
    const primaryText = style?.primaryText || '#ffffff';
    const secondaryBorderColor = style?.secondaryBorderColor || '#cbd5e1';
    const secondaryTextColor = style?.secondaryTextColor || '#0f172a';
    const borderRadius = style?.borderRadius ?? 32;

    const hasExplicitRecommendations = !!recommendedProducts?.length;
    const querySource = recommendationsQuery?.source;
    const queryManualIds = recommendationsQuery?.manualProductIds?.join(',');
    const queryCategoryId = recommendationsQuery?.categoryId;
    const queryLimit = recommendationsQuery?.limit;
    const queryExcludeId = recommendationsQuery?.excludeProductId;

    useEffect(() => {
        if (!isOpen || hasExplicitRecommendations) return;
        const query: RecommendationsQuery = {
            source: querySource,
            manualProductIds: queryManualIds ? queryManualIds.split(',').map(Number) : undefined,
            categoryId: queryCategoryId,
            limit: queryLimit,
            excludeProductId: queryExcludeId,
        };
        const limit = query.limit && query.limit > 0 ? query.limit : 4;
        const url = query.source === 'manual' || query.source === 'category'
            ? buildRecommendationsUrl(query)
            : `/products?limit=${query.excludeProductId ? limit + 1 : limit}`;
        if (!url) {
            setFetchedItems([]);
            return;
        }
        fetchAPI(url)
            .then((res: any) => {
                const list: RecommendedProduct[] = Array.isArray(res) ? res : res?.items || [];
                const filtered = query.excludeProductId
                    ? list.filter(p => p.id !== query.excludeProductId)
                    : list;
                setFetchedItems(filtered.slice(0, limit));
            })
            .catch(() => setFetchedItems([]));
    }, [isOpen, hasExplicitRecommendations, querySource, queryManualIds, queryCategoryId, queryLimit, queryExcludeId]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const displayProducts = recommendedProducts?.length ? recommendedProducts : fetchedItems;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-2xl bg-white p-6 md:p-8 shadow-2xl transition-all scale-100 border border-slate-100 flex flex-col gap-6"
                style={{ borderRadius: `${borderRadius}px` }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3.5">
                        <div
                            className="w-11 h-11 rounded-full text-white flex items-center justify-center shrink-0 shadow-sm"
                            style={{ backgroundColor: accentColor }}
                        >
                            <Check className="w-6 h-6 stroke-[3]" />
                        </div>
                        <div>
                            <h2 className="font-extrabold text-xl md:text-2xl text-slate-900 tracking-tight leading-tight">
                                {resolvedModalTitle}
                            </h2>
                            {brand && (
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    {brand}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors"
                        aria-label={t('product.atc_modal.close')}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Secondary Recommendations Header */}
                {displayProducts.length > 0 && (
                    <div className="space-y-3">
                        <h3 className="font-extrabold text-sm md:text-base text-slate-900">
                            {resolvedRecommendationsHeader}
                        </h3>

                        {/* 4 Cards Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {displayProducts.map((prod) => {
                                const imageUrl = getRecommendedProductImageUrl(prod);
                                return (
                                <div
                                    key={prod.id}
                                    className="border border-slate-200/80 bg-slate-50/50 rounded-2xl p-3 flex flex-col justify-between transition-all hover:border-slate-300 hover:shadow-xs"
                                >
                                    <div className="space-y-2">
                                        <div className="aspect-square w-full rounded-xl overflow-hidden bg-white border border-slate-100 flex items-center justify-center p-2">
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={prod.name}
                                                    className="w-full h-full object-contain"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-300 text-xs font-bold">
                                                    {t('product.atc_modal.no_photo')}
                                                </div>
                                            )}
                                        </div>
                                        <p className="font-bold text-xs text-slate-900 leading-tight break-words">
                                            {prod.name}
                                        </p>
                                    </div>
                                    <p className="font-extrabold text-xs text-slate-900 mt-2">
                                        {formatPrice(prod.sales_price ?? prod.price, currency)}
                                    </p>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full py-3.5 px-6 rounded-full bg-white hover:bg-slate-50 font-bold text-sm transition-all active:scale-98 shadow-xs text-center border"
                        style={{ borderColor: secondaryBorderColor, color: secondaryTextColor }}
                    >
                        {resolvedContinueShoppingText}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onClose();
                            router.push('/koszyk');
                        }}
                        className="w-full py-3.5 px-6 rounded-full font-bold text-sm transition-all active:scale-98 shadow-md text-center"
                        style={{ backgroundColor: primaryBg, color: primaryText }}
                    >
                        {resolvedCheckoutText}
                    </button>
                </div>
            </div>
        </div>
    );
}
